import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, switchMap, timeout } from 'rxjs/operators';
import { AddressDto } from 'src/app/domain/models/dto/address.dto';
import { CepResponse } from 'src/app/domain/models/interfaces/cep-response.interface';
import { environment } from 'src/environments/environment';

const CEP_TIMEOUT_MS = 3000;

@Injectable({
  providedIn: 'root',
})
export class AddressService {
  constructor(private readonly http: HttpClient) {}

  register(address: AddressDto) {
    return this.http.post<AddressDto>(
      `${environment.API_BASE_URL}/v1/addresses`,
      address
    );
  }

  getCep(cep: string): Observable<CepResponse> {
    const sanitized = cep.replace(/\D/g, '');

    return this.fetchFromViaCep(sanitized).pipe(
      catchError(() => this.fetchFromBrasilApi(sanitized)),
      catchError(() => this.fetchFromOpenCep(sanitized)),
      catchError(() => of({ error: true } as CepResponse))
    );
  }

  private fetchFromViaCep(cep: string): Observable<CepResponse> {
    return this.http
      .get<any>(`https://viacep.com.br/ws/${cep}/json/`)
      .pipe(
        timeout(CEP_TIMEOUT_MS),
        switchMap((res) => {
          if (res.erro) {
            return throwError(() => new Error('CEP not found on ViaCEP'));
          }
          return of(this.mapViaCepResponse(res));
        })
      );
  }

  private fetchFromBrasilApi(cep: string): Observable<CepResponse> {
    return this.http
      .get<any>(`https://brasilapi.com.br/api/cep/v1/${cep}`)
      .pipe(
        timeout(CEP_TIMEOUT_MS),
        switchMap((res) => of(this.mapBrasilApiResponse(res)))
      );
  }

  private fetchFromOpenCep(cep: string): Observable<CepResponse> {
    return this.http
      .get<any>(`https://opencep.com/v1/${cep}.json`)
      .pipe(
        timeout(CEP_TIMEOUT_MS),
        switchMap((res) => of(this.mapOpenCepResponse(res)))
      );
  }

  private mapViaCepResponse(res: any): CepResponse {
    return {
      postalCode: res.cep,
      street: res.logradouro || '',
      complement: res.complemento || '',
      neighborhood: res.bairro || '',
      city: res.localidade || '',
      state: res.uf || '',
    };
  }

  private mapBrasilApiResponse(res: any): CepResponse {
    return {
      postalCode: res.cep,
      street: res.street || '',
      complement: '',
      neighborhood: res.neighborhood || '',
      city: res.city || '',
      state: res.state || '',
    };
  }

  private mapOpenCepResponse(res: any): CepResponse {
    return {
      postalCode: res.cep,
      street: res.logradouro || '',
      complement: res.complemento || '',
      neighborhood: res.bairro || '',
      city: res.localidade || '',
      state: res.uf || '',
    };
  }
}
